import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import api from "@/lib/api";
import { ShieldWarning, ArrowSquareOut, ClockCounterClockwise, ArrowRight } from "@phosphor-icons/react";

const RESULT_STYLES = {
  no_apparent_match: { cls: "text-emerald-300 border-emerald-500/25 bg-emerald-500/10", label: "No apparent match" },
  possible_match_manual_review: { cls: "text-rose-300 border-rose-500/25 bg-rose-500/10", label: "Possible match — manual review" },
  unable_to_verify: { cls: "text-amber-300 border-amber-500/25 bg-amber-500/10", label: "Unable to verify" },
};

export default function Verifications() {
  const [verifications, setVerifications] = useState(null);
  const [auditLog, setAuditLog] = useState(null);

  useEffect(() => {
    api.get("/sorv-verifications").then((r) => setVerifications(r.data));
    api.get("/audit-log").then((r) => setAuditLog(r.data));
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-label text-orange-400 mb-2">SAFEGUARDING</div>
          <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">Registry Verifications</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            NSOPW searches logged by your compliance administrators, plus a full audit trail of every verification event.
          </p>
        </div>
        <a
          href="https://www.nsopw.gov/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="verifications-nsopw-link"
          className="rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-200 px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 hover:bg-orange-500/15"
        >
          <ArrowSquareOut size={14} weight="bold" /> Open NSOPW search
        </a>
      </div>

      {/* Warning banner */}
      <div className="rounded-2xl border border-rose-500/25 bg-rose-500/5 p-5 mb-8 flex gap-3" data-testid="verifications-legal-banner">
        <ShieldWarning size={20} weight="bold" className="text-rose-400 shrink-0 mt-0.5" />
        <div className="text-sm text-rose-200 leading-relaxed">
          <span className="font-semibold text-rose-100">Legal notice.</span> AuditReady Sports does NOT scrape or copy data from any government registry.
          A name match alone is never sufficient basis for adverse action. Verify identity through additional means, keep manual review notes,
          and consult legal counsel before any employment decision.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Verifications list */}
        <div className="rounded-2xl border border-white/10 bg-slate-900 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="font-label text-slate-500">VERIFICATIONS</div>
              <h2 className="font-display font-black tracking-tight text-lg">
                {verifications?.length || 0} search{(verifications?.length || 0) === 1 ? "" : "es"} on file
              </h2>
            </div>
          </div>
          {!verifications ? (
            <div className="p-8 text-slate-500 text-sm">Loading…</div>
          ) : verifications.length === 0 ? (
            <div className="p-12 text-center" data-testid="verifications-empty">
              <ShieldWarning size={40} weight="duotone" className="text-slate-600 mx-auto mb-3" />
              <div className="text-slate-300 font-semibold">No verifications logged yet</div>
              <div className="text-slate-500 text-sm mt-1 max-w-md mx-auto">
                Open a staff member and use the "Search &amp; Log Verification" button to search NSOPW and record the outcome.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {verifications.map((v) => {
                const style = RESULT_STYLES[v.result] || RESULT_STYLES.unable_to_verify;
                return (
                  <Link
                    key={v.id}
                    to={`/staff/${v.staff_id}`}
                    data-testid={`verification-row-${v.id}`}
                    className="flex items-center gap-4 p-5 hover:bg-white/[0.02] transition-colors"
                  >
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${style.cls}`}>
                      {style.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-100 truncate">{v.staff_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        <span className="capitalize">{v.staff_role || "staff"}</span> · {v.jurisdiction} · searched {format(new Date(v.searched_at), "MMM d, yyyy")} by {v.reviewer_name}
                      </div>
                    </div>
                    <ArrowRight size={14} weight="bold" className="text-slate-500" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Audit log */}
        <div className="rounded-2xl border border-white/10 bg-slate-900 p-5 max-h-[70vh] overflow-y-auto" data-testid="audit-log-panel">
          <div className="flex items-center gap-2 mb-3">
            <ClockCounterClockwise size={16} weight="bold" className="text-orange-400" />
            <div>
              <div className="font-label text-slate-500">AUDIT LOG</div>
              <h3 className="font-display font-black tracking-tight text-base">{auditLog?.length || 0} events</h3>
            </div>
          </div>
          {!auditLog ? (
            <div className="text-slate-500 text-sm">Loading…</div>
          ) : auditLog.length === 0 ? (
            <div className="text-slate-500 text-xs">No events yet.</div>
          ) : (
            <div className="space-y-3">
              {auditLog.map((e) => (
                <div key={e.id} className="rounded-lg border border-white/5 bg-slate-950 p-3">
                  <div className="text-xs text-slate-200 font-semibold capitalize">
                    {e.action.replace(/_/g, " ")}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {e.actor_name || e.actor_email} · {new Date(e.created_at).toLocaleString()}
                  </div>
                  {e.staff_name && (
                    <div className="text-[11px] text-slate-400 mt-0.5">Staff: {e.staff_name}</div>
                  )}
                  {e.metadata?.result && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Result: {String(e.metadata.result).replace(/_/g, " ")} · {e.metadata?.jurisdiction || ""}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
